<template>
  <div
    class="p-4 card"
    :class="
      editCommentId === comment.id
        ? 'comment-editing'
        : replyCommentId === comment.id
        ? 'comment-reply'
        : 'comment'
    "
    @mouseover="isHover = true"
    @mouseleave="isHover = false"
  >
    <b-overlay>
      <!-- Author/Role ---- Time  -->
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span class="font-weight-bolder mr-2">{{ $store.getters.getUsernameById(comment.creator) }}</span>
        <div class="d-flex align-items-center">
          <span
            class="btn btn-clean btn-xs btn-icon"
            @click.stop="$emit('toggle-comment-mark', comment.id)"
            data-placement="top"
            title="Markieren"
          >
            <i class="flaticon-star icon-1x" :class="{ 'text-warning': comment.isMarked }"></i>
            <span
              title="Anzahl markierter Antworten"
              class="amount-marked text-warning ml-1 mr-2"
              style="font-weight: bold"
              v-if="amountMarkedAnwsers"
            >
              {{ amountMarkedAnwsers }}
            </span>
          </span>
          <span
            @click="$emit('comment-delete', comment.id)"
            v-if="comment.creator === currentUserId"
            class="btn btn-clean btn-xs btn-icon"
            title="Löschen"
          >
            <i class="flaticon2-rubbish-bin icon-1x"></i>
          </span>
          <span
            v-if="currentUserId === comment.creator"
            class="btn btn-clean btn-xs btn-icon"
            @click="$emit('comment-edit', comment.id, comment.text)"
            title="Bearbeiten"
          >
            <i class="flaticon-edit icon-1x"></i>
          </span>
          <span
            class="btn btn-clean btn-xs btn-icon"
            @click.stop="copyComment(comment)"
            data-placement="top"
            title="Kopieren"
          >
            <i :class="copied === true ? 'fas fa-check icon-1x' : 'far fa-copy icon-1x'"></i>
          </span>
          <span
            v-if="!isAnswer"
            class="mr-1 btn btn-clean btn-xs btn-icon"
            title="Antworten"
            @click="$emit('comment-reply', comment.id)"
          >
            <i class="flaticon-reply icon-1x"></i>
          </span>
          <div class="font-weight-bold text-muted">
            {{ comment.lastChangeDatetime | dateTime }}
          </div>
        </div>
      </div>

      <!-- Comment HTML -->
      <!-- Überprüft, ob der Kommentar zu lang ist und wenn ja, wird er abgeschnitten -->
      <div
        ref="commentText"
        :class="{ expanded: isExpanded }"
        style="font-size: 14px"
        v-html="comment.text"
        :style="[cssProps, !isExpanded && kommentarZuLang ? { maxHeight: '200px', overflow: 'hidden' } : {}]"
      ></div>

      <!-- Weniger und Mehr anzeigen button, wenn der Kommentar zu lang ist -->
      <div v-if="kommentarZuLang" class="expand-button-wrapper">
        <button
          class="btn btn-sm btn-light-primary font-weight-bold py-1 px-5 expand-button"
          @click="toggleExpand"
        >
          {{ isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen' }}
        </button>
      </div>

      <template v-if="isAnswersShowing">
        <Comment
          class="mb-1"
          v-for="answer in filteredAnswers"
          :key="answer.id"
          :comment="answer"
          @comment-edit="$emit('comment-edit', answer.id, answer.text)"
          @comment-delete="$emit('comment-delete', answer.id)"
          @toggle-comment-mark="$emit('toggle-comment-mark', answer.id)"
          :isAnswer="true"
        ></Comment>
      </template>
      <div
        v-if="!isAnswer && isHover && !hideEmojis"
        class="shadow-sm"
        style="
          position: absolute;
          right: 0;
          bottom: 0;
          background-color: white;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: var(--radius-md);
        "
      >
        <button
          v-for="(emoji, id) in reactionEmojis"
          @click="clickEmoji(emoji)"
          :key="'emoji' + id"
          class="emoji-button p-2"
          style="border-radius: 5px"
        >
          {{ emoji }}
        </button>
      </div>

      <div v-if="comment.answers && filteredAnswers.length > 0" class="separator separator-solid"></div>
      <div class="d-flex font-size-sm">
        <span
          class="p-1 mt-1 cursor-pointer text-hover-primary rounded"
          @click.stop="isAnswersShowing = !isAnswersShowing"
          v-if="comment.answers && filteredAnswers.length > 0"
        >
          {{
            isAnswersShowing
              ? 'Antworten verstecken'
              : '+' + filteredAnswers.length + ' Antwort' + (filteredAnswers.length === 1 ? '' : 'en')
          }}
        </span>
      </div>

      <div v-if="!isAnswer && getReactions?.length > 0" class="mt-3">
        <button
          v-for="(obj, emoji) in getCountedAndSortedReaction"
          @click="clickEmoji(emoji)"
          :key="'emoji-count' + emoji + ' ' + comment.id"
          :id="'emoji-count' + emoji + ' ' + comment.id"
          style="position: relative"
          class="reaction-button mr-1 px-2"
          :class="{ 'clicked-from-user': isClickedFromUser(emoji) }"
        >
          {{ emoji }}
          <b>{{ obj.count }}</b>

          <b-tooltip
            :target="'emoji-count' + emoji + ' ' + comment.id"
            placement="top"
            :delay="0"
            triggers="hover"
          >
            <b>{{ getReactionTooltip(obj) }}</b>
          </b-tooltip>
        </button>
      </div>
    </b-overlay>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import Comment from '@/components/comments/comment.vue';
export default {
  name: 'Comment',
  props: {
    comment: { type: Object },
    isAnswer: { type: Boolean, default: false },
    hideEmojis: { type: Boolean, default: false },
  },
  components: { Comment },
  data() {
    return {
      isExpanded: false,
      isAnswersShowing: false,
      reactionEmojis: ['👍', '👎', '❤️', '🎉', '😃', '😟'],
      isHover: false,
      copied: false,
      kommentarZuLang: false,
    };
  },
  watch: {
    'comment.text': {
      handler() {
        this.$nextTick(() => {
          this.checkCommentOverflow();
        });
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['currentUserId']),
    ...mapState({
      editCommentId: state => state.comments.editCommentId,
      replyCommentId: state => state.comments.replyCommentId,
    }),
    amountMarkedAnwsers() {
      return this.comment?.answers?.filter(answer => answer.isMarked)?.length || 0;
    },
    filteredAnswers() {
      return this.comment.answers.filter(item => !this.reactionEmojis.includes(item.text));
    },
    getReactions() {
      const reactions = this.comment.answers?.filter(item => this.reactionEmojis.includes(item.text));
      return reactions;
    },
    hasUserReacted() {
      const reactions = this.getReactions;
      return reactions.find(item => item.creator === this.currentUserId);
    },
    getCountedAndSortedReaction() {
      const reactions = this.comment.answers?.filter(item => this.reactionEmojis.includes(item.text));
      const reactionsSortedByDate = reactions.sort(
        (a, b) => new Date(a.createdDateUtc) - new Date(b.createdDateUtc)
      );
      const countedReactions = reactionsSortedByDate.reduce((acc, curr) => {
        if (acc.hasOwnProperty(curr.text)) {
          const addedCount = acc[curr.text].count + 1;
          const shortName = this.getShortenedNameFromId(curr.creator);
          const peopleReactingNew = [shortName, ...acc[curr.text].peopleReacting];
          acc[curr.text] = { count: addedCount, peopleReacting: peopleReactingNew };
        } else {
          const shortName = this.getShortenedNameFromId(curr.creator);
          acc[curr.text] = { count: 1, peopleReacting: [shortName] };
        }
        return acc;
      }, {});
      const countsArray = Object.entries(countedReactions).sort((a, b) => b.count - a.count);
      const sortedCounts = Object.fromEntries(countsArray);
      return sortedCounts;
    },
    cssProps() {
      return {
        '--background-color-mentions': this.comment.text.includes(this.currentUserId) ? '#3699FF' : '#c6d1da',
        '--color-mentions': this.comment.text.includes(this.currentUserId) ? 'white' : '',
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      // Verzögerung hinzufügen, um sicherzustellen, dass der DOM vollständig geladen ist
      setTimeout(() => {
        this.checkCommentOverflow();
      }, 100);

      // Beobachter hinzufügen für dynamische Inhalte
      window.addEventListener('resize', this.checkCommentOverflow);

      // MutationObserver für Änderungen im Kommentar-Inhalt
      if (this.$refs.commentText) {
        const observer = new MutationObserver(this.checkCommentOverflow);
        observer.observe(this.$refs.commentText, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkCommentOverflow);
  },
  methods: {
    checkCommentOverflow() {
      // wartet, bis der DOM aktualisiert ist
      this.$nextTick(() => {
        if (this.$refs.commentText) {
          const computedHeight = this.$refs.commentText.scrollHeight;
          // Prüfe, ob der Inhalt größer als 200px ist
          this.kommentarZuLang = computedHeight > 200;
        }
      });
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    getShortenedNameFromId(id) {
      const fullName = this.$store.getters.getUsernameById(id);
      const splitName = fullName?.split(' ');
      // the last entity from split is the last name (in case of multiple names "de las hereras")
      const splitNameLength = splitName.length - 1;
      const lastNameShortened = splitName[splitNameLength].substring(0, 1) + '.';
      const shortName = splitName[0] + ' ' + lastNameShortened;
      return shortName;
    },
    getReactionTooltip(obj) {
      if (obj.count > 2) {
        const lastReactingPerson = obj.peopleReacting[0];
        return `${lastReactingPerson} und ${obj.count - 1} weitere`;
      } else if (obj.count === 2) {
        return `${obj.peopleReacting[0]}, ${obj.peopleReacting[1]}`;
      } else {
        return `${obj.peopleReacting[0]}`;
      }
    },
    isClickedFromUser(emoji) {
      const reactions = this.getReactions;
      const filteredReactions = reactions.filter(
        item => item.text === emoji && item.creator === this.currentUserId
      );
      return filteredReactions.length > 0;
    },
    async clickEmoji(emoji) {
      if (this.hasUserReacted) {
        const reactionComment = this.getReactions.find(item => item.creator === this.currentUserId);
        // check if it is the same emoji, just delete the reaction
        if (reactionComment.text === emoji) {
          this.$emit('delete-reaction', { reactionCommentId: reactionComment.id });
        }
        // if it is a different reaction, save new one, delete old one
        else {
          this.$emit('change-reaction', {
            oldReactionCommentId: reactionComment.id,
            replyCommentId: this.comment.id,
            emoji,
          });
        }
      } else {
        this.$emit('save-reaction', { replyCommentId: this.comment.id, emoji });
      }
    },
    copyComment(comment) {
      const type = 'text/html';
      const blob = new Blob([comment.text], { type });
      const { ClipboardItem } = window;
      const data = [new ClipboardItem({ [type]: blob })];
      navigator.clipboard
        .write(data)
        .then(() => {
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 1500);
        })
        .catch(err => {
          console.log(err);
        });
    },
  },
};
</script>
<style scoped>
.comment {
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
}

.expand-button-wrapper {
  position: relative;
  margin-top: -5px;
  text-align: center;
  padding-bottom: 5px;
}

.expand-button {
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
}

:deep(table) {
  border-collapse: collapse;
  margin: 0;
  overflow: hidden;
  table-layout: fixed;
  width: 100%;
}

:deep(td, th) {
  border: 1px solid rgba(61, 37, 20, 0.12);
  box-sizing: border-box;
  min-width: 1em;
  padding: 6px 8px;
  position: relative;
  vertical-align: top;

  > * {
    margin-bottom: 0;
  }
}

:deep(th) {
  background-color: rgba(61, 37, 20, 0.05);
  font-weight: bold;
  text-align: left;
}

.comment-editing {
  background-color: rgba(4, 96, 255, 0.13);
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
}
.comment-reply {
  background-color: #fdfd96;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
}
.emoji-button,
.reaction-button {
  background: none;
  color: inherit;
  border: none;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  font-size: 12px;
}
.emoji-button:hover,
.reaction-button:hover {
  background-color: var(--border);
}
.reaction-button {
  border-radius: var(--radius-sm);
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.clicked-from-user {
  background-color: rgba(222, 236, 249);
  border: 1px solid rgba(0, 120, 212, 1);
}
:deep(.mention) {
  background-color: var(--background-color-mentions);
  color: var(--color-mentions);
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 500;
  margin-right: 4px;
}
</style>
